import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as uuid from 'uuid';
import { UserExperiment } from '@blisspointmedia/bpm-types/dist/FeatureExperiments';
import { tokenDiffRenderer } from '@src/Components/PendingChanges/pending-changes-utils';
import { ExperimentTable } from './ExperimentTable';
import { GroupedDataRow } from './types';
import {
  EMPTY_CELL_TEXT,
  formatExperimentsDate,
  MAX_OVERLAY_LENGTH,
  MAX_SHOWN_GROUPED_USER_EMAILS
} from './utils';
import './ExperimentTable.scss';

const userColumnRenderer = (data: GroupedDataRow<UserExperiment, 'user_email'>) => {
  if (!data.user_email) {
    return <div className='experiment-cell-text'>{EMPTY_CELL_TEXT}</div>;
  }

  if (data.user_email.length <= MAX_SHOWN_GROUPED_USER_EMAILS) {
    return <div className='experiment-cell-text'>{data.user_email.join(', ')}</div>;
  }

  const sortedUserEmails = [...data.user_email].sort();
  const shownUserEmails = sortedUserEmails.slice(0, MAX_SHOWN_GROUPED_USER_EMAILS);
  const remainingCount = sortedUserEmails.length - shownUserEmails.length;

  const overlayShownEmails = sortedUserEmails.slice(0, MAX_OVERLAY_LENGTH);
  const overlayRemainingCount = sortedUserEmails.length - overlayShownEmails.length;

  return (
    <OverlayTrigger
      placement='right'
      overlay={
        <Tooltip id={uuid.v4()} className='experiment-overlay-trigger'>
          <div className='tooltip-text-container'>
            {overlayShownEmails.map(userEmail => (
              <div key={userEmail}>{userEmail}</div>
            ))}
            {overlayRemainingCount > 0 && (
              <div className='tooltip-item'>{`+ ${overlayRemainingCount} more`}</div>
            )}
          </div>
        </Tooltip>
      }
    >
      <div className='experiment-cell-text'>
        {`${shownUserEmails.join(', ')}${remainingCount > 0 ? ` + ${remainingCount} more` : ''}`}
      </div>
    </OverlayTrigger>
  );
};

const headers = [
  {
    label: 'Experiment Flag',
    field: 'experiment_flag',
    type: 'select',
    options: 'experiment_flag',
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (experimentFlag: string) => !experimentFlag || experimentFlag.length === 0
  },
  {
    label: 'Users With Access',
    field: 'user_email',
    type: 'select',
    options: 'user_email',
    isMulti: true,
    flex: 7,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (userEmail: string) => !userEmail || userEmail.length === 0,
    renderer: userColumnRenderer,
    diffRenderer: tokenDiffRenderer<GroupedDataRow<UserExperiment, 'user_email'>>('user_email')
  },
  {
    label: 'Last Modified',
    field: 'lastmodified',
    type: 'text',
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    renderer: (data: UserExperiment) =>
      data.lastmodified ? (
        <div>{formatExperimentsDate(data.lastmodified)}</div>
      ) : (
        <div>{EMPTY_CELL_TEXT}</div>
      ),
    uneditable: true
  },
  {
    label: 'Modified By',
    field: 'lastuser',
    type: 'text',
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    uneditable: true
  }
];

const USER_GROUPS_EXPECTED_KEYS: (keyof UserExperiment & string)[] = [
  'experiment_flag',
  'user_email',
  'created',
  'lastmodified'
];

const createNewUserExperiment = (groupByColumn: string, groupedColumn: string): UserExperiment => {
  const now = new Date().toISOString();
  return {
    experiment_flag: groupByColumn,
    user_email: groupedColumn,
    created: now,
    lastmodified: now
  };
};

export const UserExperiments = () => (
  <ExperimentTable<UserExperiment>
    options={{
      headers,
      name: 'User Experiments',
      addButtonText: 'Add Experiment',
      createNewRow: createNewUserExperiment,
      groupByColumn: 'experiment_flag',
      groupedColumn: 'user_email',
      expectedKeys: USER_GROUPS_EXPECTED_KEYS,
      jsonField: 'user_experiments',
      modalTitle: 'New User Experiment'
    }}
  />
);
